import { yupResolver } from '@hookform/resolvers/yup';
import { ImagePlus, Upload01 } from '@untitled-ui/icons-react/build/cjs';
import { isValidPhoneNumber } from 'libphonenumber-js';
import React, { forwardRef, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ImageWithFallback from 'src/components/ImageWithFallback';
import SelectBox from 'src/components/SelectBox/SelectBox';
import { RootState } from 'src/redux/store';
import * as yup from 'yup';

import CropperModal from '../../../components/CropperModal/CropperModal';
import InputText from '../../../components/InputText/InputText';
import PhoneInput from '../../../components/PhoneInput/PhoneInput';
import WalToast from '../../../utils/WalToast';

export interface PersonalInfoFormData {
    firstName?: string;
    lastName?: string;
    title?: string;
    phone?: string;
    defaultLanguage?: string;
    profilePicture?: string;
}

interface PersonalInfoProps {
    // Step 1 no longer calls the API itself — the parent collects this data and
    // submits it together with step 2 (ShippingProfile). imageUrl = cropped base64 or ''.
    onSubmit: (data: PersonalInfoFormData, imageUrl: string) => void;
    // Provided when the user navigates Back from step 2 so their edits survive.
    initialValues?: PersonalInfoFormData;
    initialImageUrl?: string;
    isLoading: boolean;
    setIsFormValid?: (isValid: boolean) => void;
}

const schema = (t: (key: string) => string): yup.ObjectSchema<PersonalInfoFormData> =>
    yup.object().shape({
        firstName: yup.string().required(t('SetupAccount.PersonalInfo.Validation.First_Name_Required')),
        lastName: yup.string().required(t('SetupAccount.PersonalInfo.Validation.Last_Name_Required')),
        title: yup.string().required(t('SetupAccount.PersonalInfo.Validation.Title_Required')),
        phone: yup
            .string()
            .required(t('SetupAccount.PersonalInfo.Validation.Phone_Required'))
            .test('is-valid-phone', t('SetupAccount.PersonalInfo.Validation.Phone_Invalid'), (value) => {
                if (!value) return false;

                try {
                    return isValidPhoneNumber(`+${value}`);
                } catch (error) {
                    console.log('🚀 ~ .test ~ error:', error);

                    return false;
                }
            }),
        defaultLanguage: yup.string().required(t('SetupAccount.PersonalInfo.Validation.Language_Required')),
        profilePicture: yup.string().optional(),
    });

const defaultValues: PersonalInfoFormData = {
    firstName: '',
    lastName: '',
    title: '',
    phone: '',
    // Must match a getLanguageOptions value ('en'|'fr'|'es'), otherwise the SelectBox shows blank.
    defaultLanguage: 'en',
    profilePicture: '',
};

const getLanguageOptions = (t: (key: string) => string) => [
    {
        value: 'en',
        label: t('SetupAccount.PersonalInfo.Language.Options.English'),
    },
    {
        value: 'fr',
        label: t('SetupAccount.PersonalInfo.Language.Options.French'),
    },
    {
        value: 'es',
        label: t('SetupAccount.PersonalInfo.Language.Options.Spanish'),
    },
];

const PersonalInfo = forwardRef<HTMLFormElement, PersonalInfoProps>(({ onSubmit, initialValues, initialImageUrl = '', isLoading, setIsFormValid }, ref) => {
    const { t } = useTranslation();
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    const languageOptions = getLanguageOptions(t);
    const [showCropper, setShowCropper] = useState(false);
    const [imageUrl, setImageUrl] = useState(initialImageUrl);
    const [uploadImageUrl, setUploadImageUrl] = useState('');

    // Store initial values for comparison
    const initialFormValues = {
        firstName: currentUser?.firstName || '',
        lastName: currentUser?.lastName || '',
        title: currentUser?.title || '',
        phone: currentUser?.phone || '',
        defaultLanguage: currentUser?.language || '',
        profilePicture: `${currentUser?.imageUrl}${currentUser?.image}` || null,
    };

    const {
        control,
        handleSubmit,
        watch,
        formState: { isSubmitted, isSubmitting },
        trigger,
    } = useForm<PersonalInfoFormData>({
        resolver: yupResolver(schema(t)) as any,
        // Priority: edits kept by the parent (Back from step 2) > existing contact record > blank.
        defaultValues:
            initialValues ??
            (currentUser
                ? {
                      firstName: initialFormValues.firstName,
                      lastName: initialFormValues.lastName,
                      title: initialFormValues.title,
                      phone: initialFormValues.phone,
                      defaultLanguage: initialFormValues.defaultLanguage,
                      profilePicture: initialFormValues.profilePicture,
                  }
                : defaultValues),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    // Watch form values for changes
    const currentFormValues = watch();

    // Check form validity and notify parent
    useEffect(() => {
        if (setIsFormValid) {
            let isValid = Boolean(
                currentFormValues.firstName?.trim() &&
                    currentFormValues.lastName?.trim() &&
                    currentFormValues.title?.trim() &&
                    currentFormValues.phone?.trim() &&
                    currentFormValues.defaultLanguage?.trim()
            );

            // Also check if the phone number is valid using the schema validation
            if (isValid && currentFormValues.phone) {
                try {
                    isValid = isValid && isValidPhoneNumber(`+${currentFormValues.phone}`);
                } catch (error) {
                    isValid = false;
                }
            }

            setIsFormValid(isValid);
        }
    }, [currentFormValues, setIsFormValid]);

    const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg'];

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Reset input so the same file can be reselected after an error
        event.target.value = '';

        if (!file) return;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            WalToast.error(t('Common.File_must_be_image'));

            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();

            img.onload = () => {
                if (img?.width > 800 || img?.height > 800) {
                    WalToast.error('Cannot exceed 800x800 pixels');

                    return;
                }
                setUploadImageUrl(e.target!.result as string);
                setShowCropper(true);
            };

            img.onerror = () => {
                WalToast.error(t('Common.File_Corrupted'));
            };

            img.src = e.target!.result as string;
        };

        reader.readAsDataURL(file);
    };

    // Validation passed — hand the data to the parent; the API call happens after step 2.
    const handleFormSubmit = (data: PersonalInfoFormData) => {
        onSubmit(data, imageUrl);
    };

    return (
        <>
            <form ref={ref} onSubmit={handleSubmit(handleFormSubmit)} noValidate>
                <div className="flex flex-col gap-y-5">
                    {/* Profile Picture Upload */}
                    <div>
                        <p className="form_label block mb-1.5">{t('SetupAccount.PersonalInfo.Profile_Picture.Label')}</p>
                        <div className="flex items-center space-x-4 justify-between">
                            <div className="w-[48px] h-[48px] min-w-[48px] min-h-[48px] flex justify-center items-center rounded-[10px] bg-white shadow-xs border border-utilityGray200 max-w-[48px] max-h-[48px] overflow-hidden">
                                {imageUrl || currentUser?.image ? (
                                    <ImageWithFallback
                                        src={imageUrl || `${currentUser?.imageUrl}${currentUser?.image}`}
                                        alt={`${currentUser?.firstName} ${currentUser?.lastName}`}
                                        className="w-full h-full object-cover rounded-[10px]"
                                    />
                                ) : (
                                    <ImagePlus className="w-5 h-5 flex-none text-textSecoundary" />
                                )}
                            </div>
                            <input type="file" accept="image/png,image/jpeg" onChange={handleImageUpload} className="hidden" id="profile-upload" disabled={isLoading} />
                            <label htmlFor="profile-upload" className="btn_secondary_black btn no-animation text-xs leading-5 min-h-[unset] h-auto font-semibold px-4 py-2 shadow-sm !outline-0">
                                <Upload01 className="w-4 h-4" />
                                {t('SetupAccount.PersonalInfo.Profile_Picture.Upload')}
                            </label>
                        </div>
                    </div>

                    <Controller
                        name="firstName"
                        control={control}
                        render={({ field, fieldState }) => (
                            <InputText
                                inputName="firstName"
                                labelClassName="block mb-1.5"
                                label={t('SetupAccount.PersonalInfo.First_Name.Label')}
                                placeholder={t('SetupAccount.PersonalInfo.First_Name.Placeholder')}
                                value={field.value}
                                onChangeFunc={(e) => field.onChange(e.target.value)}
                                onBlur={field.onBlur}
                                errorText={fieldState.error?.message ? true : false}
                                required
                                parentClassName="flex-1"
                                disabled={isLoading}
                                maxLength={50}
                            />
                        )}
                    />

                    <Controller
                        name="lastName"
                        control={control}
                        render={({ field, fieldState }) => (
                            <InputText
                                inputName="lastName"
                                labelClassName="block mb-1.5"
                                label={t('SetupAccount.PersonalInfo.Last_Name.Label')}
                                placeholder={t('SetupAccount.PersonalInfo.Last_Name.Placeholder')}
                                value={field.value}
                                onChangeFunc={(e) => field.onChange(e.target.value)}
                                onBlur={field.onBlur}
                                errorText={fieldState.error?.message ? true : false}
                                required
                                parentClassName="flex-1"
                                disabled={isLoading}
                                maxLength={50}
                            />
                        )}
                    />

                    <Controller
                        name="title"
                        control={control}
                        render={({ field, fieldState }) => (
                            <InputText
                                inputName="title"
                                labelClassName="block mb-1.5"
                                label={t('SetupAccount.PersonalInfo.Job_Title.Label')}
                                placeholder={t('SetupAccount.PersonalInfo.Job_Title.Placeholder')}
                                value={field.value}
                                onChangeFunc={(e) => field.onChange(e.target.value)}
                                onBlur={field.onBlur}
                                errorText={fieldState.error?.message ? true : false}
                                required
                                disabled={isLoading}
                            />
                        )}
                    />

                    <Controller
                        name="phone"
                        control={control}
                        render={({ field, fieldState }) => (
                            <PhoneInput
                                phoneInputParentClassName="create-account-phone-input"
                                label={t('SetupAccount.PersonalInfo.Phone_Number.Label')}
                                required
                                value={field.value ? `+${field.value}` : ''}
                                onNumberChange={(country: any, number: string, code: string) => {
                                    const fullPhone = number ? `${code}${number}` : '';
                                    field.onChange(fullPhone);

                                    if (isSubmitted) {
                                        trigger('phone');
                                    }
                                }}
                                errors={fieldState.error?.message || ''}
                                isButtonClicked={isSubmitted || isSubmitting}
                                disabled={isLoading}
                                name="phone"
                            />
                        )}
                    />

                    <Controller
                        name="defaultLanguage"
                        control={control}
                        render={({ field, fieldState }) => (
                            <SelectBox
                                label={t('SetupAccount.PersonalInfo.Language.Label')}
                                labelClassName="form_label block mb-1.5"
                                parentClassName="select-height-34"
                                value={languageOptions.find((option) => option.value === field.value) || null}
                                onChangeFunc={(selected: any) => {
                                    if (!Array.isArray(selected) && selected?.value) {
                                        field.onChange(selected.value);
                                    } else {
                                        field.onChange('');
                                    }
                                }}
                                options={languageOptions}
                                placeholder={t('SetupAccount.PersonalInfo.Language.Placeholder')}
                                errorText={fieldState.error?.message ? true : false}
                                isDisabled={isLoading}
                            />
                        )}
                    />
                </div>
            </form>

            {showCropper && (
                <CropperModal
                    imageUrl={uploadImageUrl}
                    imageUpload={(croppedImage: string) => {
                        setImageUrl(croppedImage);
                        setShowCropper(false);
                    }}
                    setUpload={setShowCropper}
                    setCropData={() => {}}
                    isCropType={false}
                    defaultCropType="Square"
                    modalTitle={t('SetupAccount.PersonalInfo.Cropper.Title')}
                    modalDesc={t('SetupAccount.PersonalInfo.Cropper.Description')}
                />
            )}
        </>
    );
});

PersonalInfo.displayName = 'PersonalInfo';

export default PersonalInfo;
